import type { BookChapter } from '@/components/books/book';
import type { BookCardBook } from '@/components/books/book-card';
import {
    BookTocActionsContext,
    BookTocBusyContext,
    BookTocDataContext,
    type BookTocActions,
    type BookTocBusy,
    type BookTocData,
} from '@/components/books/toc/book-toc-contexts';
import { createTocReorderFlush } from '@/hooks/use-toc-reorder-flush';
import { chapterDropOverIndex, splitTocChapters } from '@/lib/about-page';
import type { SectionPlacement } from '@/lib/add-ma-section';
import type { MaDeletedSectionRow, MaPlacement, MaSharedBookRef, MaUnplacedSectionRow } from '@/lib/ma-books-api';
import {
    renameMaChapter,
    renameMaSection,
    setMaChapterReleaseExclusion,
    setMaSectionReleaseExclusion,
    trashMaChapter,
    trashMaSection,
} from '@/lib/ma-books-api';
import { syncAboutPageByDocumentId, syncSectionByDocumentId } from '@/lib/sync-to-zybook';
import {
    applyChapterDrop,
    applySectionDrop,
    chapterBlockIdsUnchanged,
    chapterTreesEqual,
    findChapterContainingSection,
    mergeSectionAfterAdd,
    remapExpandedChapterIds,
    removeChapterAfterTrash,
    removeSectionAfterTrash,
    renumberChapterTree,
    resolveChapterIdAfterRenumber,
    sectionMoveDeletesSourceChapter,
} from '@/lib/toc-tree';
import type { BookTocLoaderData } from '@/pages/book-toc.loader';
import authService from '@/services/zybook-auth-service';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMatches, useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';

export function computeBookTocIsBusy({
    inFlight,
    trashingSectionId,
    trashingChapterId,
    renamingId,
    excludingId,
}: {
    inFlight: boolean;
    trashingSectionId: string | null;
    trashingChapterId: string | null;
    renamingId: string | null;
    excludingId: string | null;
}): boolean {
    return inFlight || trashingSectionId !== null || trashingChapterId !== null || renamingId !== null || excludingId !== null;
}

function findSectionById(chapters: BookChapter[], sectionId: string) {
    for (const chapter of chapters) {
        const section = chapter.sections.find(item => item.id === sectionId);
        if (section) {
            return section;
        }
    }
    return null;
}

function findChapterById(chapters: BookChapter[], chapterId: string) {
    return chapters.find(chapter => chapter.id === chapterId) ?? null;
}

type TocMoveToastInfo = {
    title: string;
    destination: string;
};

function sectionMoveDestination(chapters: BookChapter[], sectionId: string): string | null {
    const section = findSectionById(chapters, sectionId);
    return section ? `section ${section.number}` : null;
}

function chapterMoveDestination(chapters: BookChapter[], chapter: BookChapter): string | null {
    const movedChapter = chapters.find(entry => entry.ma_zybook_chapter_id === chapter.ma_zybook_chapter_id);
    return movedChapter ? `chapter ${movedChapter.number}` : null;
}

function showTocOrderSavedToast(info: TocMoveToastInfo | null) {
    if (info) {
        toast.success(info.title, {
            description: `Moved to ${info.destination}`,
        });
        return;
    }

    toast.success('Table of contents order was updated.', {
        description: 'Order saved',
    });
}

const MA_TITLE_MAX_LEN = 255; // db varchar max

function applySectionRename(chapters: BookChapter[], sectionId: string, title: string, shortTitle: string | null): BookChapter[] {
    return chapters.map(chapter => ({
        ...chapter,
        sections: chapter.sections.map(section => (section.id === sectionId ? { ...section, title, shortTitle } : section)),
    }));
}

function applyChapterRename(chapters: BookChapter[], chapterId: string, title: string, shortTitle: string | null): BookChapter[] {
    return chapters.map(chapter => (chapter.id === chapterId ? { ...chapter, title, shortTitle } : chapter));
}

function applySectionReleaseExclusion(
    chapters: BookChapter[],
    sectionId: string,
    placement: MaPlacement,
    testBankPlacement: MaPlacement,
): BookChapter[] {
    return chapters.map(chapter => ({
        ...chapter,
        sections: chapter.sections.map(section => (section.id === sectionId ? { ...section, placement, testBankPlacement } : section)),
    }));
}

function applyChapterReleaseExclusion(chapters: BookChapter[], chapterId: string, placement: MaPlacement): BookChapter[] {
    return chapters.map(chapter =>
        chapter.id === chapterId
            ? {
                  ...chapter,
                  sections: chapter.sections.map(section => ({
                      ...section,
                      placement,
                      testBankPlacement: section.testQuestionDocumentId != null ? placement : section.testBankPlacement,
                  })),
              }
            : chapter,
    );
}

function validateRenameTitles(title: string, shortTitle: string): { title: string; shortTitle: string | null } | string {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
        return 'Title is required';
    }
    if (trimmedTitle.length > MA_TITLE_MAX_LEN) {
        return `Title must be at most ${MA_TITLE_MAX_LEN} characters`;
    }
    const trimmedShort = shortTitle.trim();
    if (trimmedShort.length > MA_TITLE_MAX_LEN) {
        return `Short title must be at most ${MA_TITLE_MAX_LEN} characters`;
    }
    return { title: trimmedTitle, shortTitle: trimmedShort || null };
}

function BookTocStateActiveProvider({
    book,
    initialChapters,
    initialUnplacedSections,
    initialDeletedSections,
    initialSharedDocuments,
    children,
}: {
    book: BookCardBook;
    initialChapters: BookChapter[];
    initialUnplacedSections: MaUnplacedSectionRow[];
    initialDeletedSections: MaDeletedSectionRow[];
    initialSharedDocuments: Record<string, MaSharedBookRef[]>;
    children: ReactNode;
}) {
    const [optimisticChapters, setOptimisticChapters] = useState(initialChapters);
    const [unplacedSections, setUnplacedSections] = useState(initialUnplacedSections);
    const [deletedSections, setDeletedSections] = useState(initialDeletedSections);
    const [sharedDocuments, setSharedDocuments] = useState(initialSharedDocuments);
    const [lastConfirmedChapters, setLastConfirmedChapters] = useState(initialChapters);
    const [expandedChapterIds, setExpandedChapterIds] = useState<string[]>([]);
    const [dirty, setDirty] = useState(false);
    const [inFlight, setInFlight] = useState(false);
    const [trashingSectionId, setTrashingSectionId] = useState<string | null>(null);
    const [trashingChapterId, setTrashingChapterId] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [excludingId, setExcludingId] = useState<string | null>(null);
    const [syncingSectionId, setSyncingSectionId] = useState<string | null>(null);

    const optimisticRef = useRef(optimisticChapters);
    const lastConfirmedRef = useRef(lastConfirmedChapters);
    const dirtyRef = useRef(dirty);
    const inFlightRef = useRef(inFlight);
    const dragSnapshotRef = useRef<BookChapter[] | null>(null);
    const lastMovedToastRef = useRef<TocMoveToastInfo | null>(null);

    const { aboutChapter, contentChapters } = useMemo(() => splitTocChapters(optimisticChapters), [optimisticChapters]);
    const isBusy = computeBookTocIsBusy({
        inFlight,
        trashingSectionId,
        trashingChapterId,
        renamingId,
        excludingId,
    });
    const canManageToc = authService.canSeeAllMAContent();
    const showSync = canManageToc && book.development !== false;

    useEffect(() => {
        optimisticRef.current = optimisticChapters;
    }, [optimisticChapters]);

    useEffect(() => {
        lastConfirmedRef.current = lastConfirmedChapters;
    }, [lastConfirmedChapters]);

    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);

    useEffect(() => {
        inFlightRef.current = inFlight;
    }, [inFlight]);

    const setDirtySync = useCallback((value: boolean) => {
        dirtyRef.current = value;
        setDirty(value);
    }, []);

    const flushRef = useRef<ReturnType<typeof createTocReorderFlush> | null>(null);

    const buildFlush = useCallback(
        () =>
            createTocReorderFlush({
                zybookCode: book.id,
                getOptimistic: () => optimisticRef.current,
                getLastConfirmed: () => lastConfirmedRef.current,
                getDirty: () => dirtyRef.current,
                setInFlight,
                setDirty: setDirtySync,
                setOptimistic: setOptimisticChapters,
                setLastConfirmed: setLastConfirmedChapters,
                onError: message => {
                    toast.error('Could not save table of contents order', {
                        description: message,
                    });
                },
                onSuccess: () => {
                    showTocOrderSavedToast(lastMovedToastRef.current);
                },
            }),
        [book.id, setDirtySync],
    );

    useEffect(() => {
        flushRef.current = buildFlush();
    }, [buildFlush]);

    const scheduleSave = useCallback(() => {
        void flushRef.current?.flushReorder();
    }, []);

    const getOptimisticChapters = useCallback(() => optimisticRef.current, []);

    const beginDragSnapshot = useCallback(() => {
        dragSnapshotRef.current = optimisticRef.current;
    }, []);

    const getDragSnapshot = useCallback(() => dragSnapshotRef.current, []);

    const previewSectionDrop = useCallback((activeSectionId: string, targetChapterId: string, overIndex: number) => {
        const snapshot = dragSnapshotRef.current;
        const keepEmptyChapters = snapshot !== null && sectionMoveDeletesSourceChapter(snapshot, activeSectionId, targetChapterId);
        const next = applySectionDrop(
            optimisticRef.current,
            activeSectionId,
            targetChapterId,
            overIndex,
            keepEmptyChapters ? { keepEmptyChapters: true } : undefined,
        );
        if (chapterTreesEqual(optimisticRef.current, next)) {
            return;
        }
        optimisticRef.current = next;
        setOptimisticChapters(next);
    }, []);

    const previewChapterDrop = useCallback((activeChapterId: string, overIndex: number): boolean => {
        const next = applyChapterDrop(optimisticRef.current, activeChapterId, overIndex, { renumberIds: false });
        if (chapterTreesEqual(optimisticRef.current, next)) {
            return false;
        }
        optimisticRef.current = next;
        setOptimisticChapters(next);
        return true;
    }, []);

    const revertDragSnapshot = useCallback(() => {
        const snapshot = dragSnapshotRef.current;
        dragSnapshotRef.current = null;
        if (!snapshot) {
            return;
        }
        optimisticRef.current = snapshot;
        setOptimisticChapters(snapshot);
    }, []);

    const clearDragSnapshot = useCallback(() => {
        dragSnapshotRef.current = null;
    }, []);

    const onSectionDrop = useCallback(
        (activeSectionId: string, targetChapterId: string, overIndex: number) => {
            dragSnapshotRef.current = null;
            const current = optimisticRef.current;
            const section = findSectionById(current, activeSectionId);
            const next = applySectionDrop(current, activeSectionId, targetChapterId, overIndex);
            const destination = section ? sectionMoveDestination(next, activeSectionId) : null;
            lastMovedToastRef.current = section && destination ? { title: section.title, destination } : null;
            optimisticRef.current = next;
            setOptimisticChapters(next);
            if (!chapterBlockIdsUnchanged(current, next)) {
                setExpandedChapterIds(expanded => remapExpandedChapterIds(current, next, expanded));
            }
            setDirtySync(true);
            scheduleSave();
        },
        [scheduleSave, setDirtySync],
    );

    const onChapterDrop = useCallback(
        (activeChapterId: string, overIndex: number) => {
            dragSnapshotRef.current = null;
            const current = optimisticRef.current;
            const adjustedIndex = chapterDropOverIndex(current, overIndex);
            const chapter = findChapterById(current, activeChapterId);
            const next = applyChapterDrop(current, activeChapterId, adjustedIndex);
            const destination = chapter ? chapterMoveDestination(next, chapter) : null;
            lastMovedToastRef.current = chapter && destination ? { title: chapter.title, destination } : null;
            optimisticRef.current = next;
            setOptimisticChapters(next);
            setExpandedChapterIds(expanded => remapExpandedChapterIds(current, next, expanded));
            setDirtySync(true);
            scheduleSave();
        },
        [scheduleSave, setDirtySync],
    );

    const confirmChapterDrop = useCallback(
        (dragStartChapterId: string, anchorSectionId: string | null) => {
            const snapshot = dragSnapshotRef.current;
            dragSnapshotRef.current = null;
            if (!snapshot) {
                return;
            }

            const current = optimisticRef.current;
            if (chapterTreesEqual(snapshot, current)) {
                return;
            }

            const renumbered = renumberChapterTree(current);
            optimisticRef.current = renumbered;
            setOptimisticChapters(renumbered);

            const activeChapterId = resolveChapterIdAfterRenumber(renumbered, dragStartChapterId, anchorSectionId);
            const chapter = findChapterById(renumbered, activeChapterId);
            const destination = chapter ? chapterMoveDestination(renumbered, chapter) : null;
            lastMovedToastRef.current = chapter && destination ? { title: chapter.title, destination } : null;
            setExpandedChapterIds(expanded => remapExpandedChapterIds(snapshot, renumbered, expanded));
            setDirtySync(true);
            scheduleSave();
        },
        [scheduleSave, setDirtySync],
    );

    const mergeSectionAfterAddHandler = useCallback(
        (
            placement: SectionPlacement,
            section: {
                id: string;
                title: string;
                shortTitle?: string;
                testQuestionDocumentId?: number | null;
                testQuestionDocumentLastEditedAt?: string | null;
                maZybookChapterId?: number;
            },
        ) => {
            const next = mergeSectionAfterAdd(optimisticRef.current, placement, section);
            optimisticRef.current = next;
            setOptimisticChapters(next);
            if (!dirtyRef.current && !inFlightRef.current) {
                const nextConfirmed = mergeSectionAfterAdd(lastConfirmedRef.current, placement, section);
                lastConfirmedRef.current = nextConfirmed;
                setLastConfirmedChapters(nextConfirmed);
            }
        },
        [],
    );

    const removeUnplacedSection = useCallback((maZybookDocumentId: number) => {
        setUnplacedSections(current => current.filter(row => row.ma_zybook_document_id !== maZybookDocumentId));
    }, []);

    const removeDeletedSection = useCallback((maZybookDocumentId: number) => {
        setDeletedSections(current => current.filter(row => row.ma_zybook_document_id !== maZybookDocumentId));
    }, []);

    const addDeletedSection = useCallback((row: MaDeletedSectionRow) => {
        setDeletedSections(current =>
            current.some(entry => entry.ma_zybook_document_id === row.ma_zybook_document_id) ? current : [...current, row],
        );
    }, []);

    const mergeSharedDocumentsHandler = useCallback((maDocumentId: string, books: MaSharedBookRef[]) => {
        setSharedDocuments(current => {
            const existing = current[maDocumentId] ?? [];
            const codes = new Set(existing.map(entry => entry.zybook_code));
            const next = [...existing];
            for (const bookRef of books) {
                if (!codes.has(bookRef.zybook_code)) {
                    next.push(bookRef);
                    codes.add(bookRef.zybook_code);
                }
            }
            return { ...current, [maDocumentId]: next };
        });
    }, []);

    const commitChapterTree = useCallback((next: BookChapter[]) => {
        optimisticRef.current = next;
        setOptimisticChapters(next);
        if (!dirtyRef.current && !inFlightRef.current) {
            lastConfirmedRef.current = next;
            setLastConfirmedChapters(next);
        }
    }, []);

    const trashSectionHandler = useCallback(
        async (sectionId: string) => {
            const chapter = findChapterContainingSection(optimisticRef.current, sectionId);
            if (chapter?.number === 0) {
                return;
            }

            const section = findSectionById(optimisticRef.current, sectionId);
            if (!section) {
                return;
            }

            try {
                setTrashingSectionId(sectionId);
                const result = await trashMaSection(book.id, Number.parseInt(sectionId, 10));
                if (result.deleted_section) {
                    addDeletedSection(result.deleted_section);
                }
                const next = removeSectionAfterTrash(optimisticRef.current, sectionId);
                commitChapterTree(next);
                setExpandedChapterIds(expanded => expanded.filter(id => next.some(ch => ch.id === id)));
                toast.success('Section moved to trash');
            } catch (err) {
                toast.error('Could not delete section', {
                    description: err instanceof Error ? err.message : String(err),
                });
            } finally {
                setTrashingSectionId(null);
            }
        },
        [addDeletedSection, book.id, commitChapterTree],
    );

    const trashChapterHandler = useCallback(
        async (chapterId: string) => {
            const chapter = findChapterById(optimisticRef.current, chapterId);
            if (!chapter || chapter.number === 0 || chapter.sections.length === 0) {
                return;
            }

            try {
                setTrashingChapterId(chapterId);
                const result = await trashMaChapter(book.id, chapter.ma_zybook_chapter_id);
                for (const row of result.deleted_sections ?? []) {
                    addDeletedSection(row);
                }
                const next = removeChapterAfterTrash(optimisticRef.current, chapterId);
                commitChapterTree(next);
                setExpandedChapterIds(expanded => expanded.filter(id => next.some(ch => ch.id === id)));
                toast.success('Chapter moved to trash');
            } catch (err) {
                toast.error('Could not delete chapter', {
                    description: err instanceof Error ? err.message : String(err),
                });
            } finally {
                setTrashingChapterId(null);
            }
        },
        [addDeletedSection, book.id, commitChapterTree],
    );

    const renameSectionHandler = useCallback(
        async (sectionId: string, titles: { title: string; shortTitle: string }) => {
            const section = findSectionById(optimisticRef.current, sectionId);
            if (!section) {
                return;
            }

            const validated = validateRenameTitles(titles.title, titles.shortTitle);
            if (typeof validated === 'string') {
                toast.error('Could not rename section', { description: validated });
                throw new Error(validated);
            }

            try {
                setRenamingId(sectionId);
                const result = await renameMaSection(book.id, Number.parseInt(sectionId, 10), {
                    section_title: validated.title,
                    short_section_title: validated.shortTitle,
                });
                const next = applySectionRename(
                    optimisticRef.current,
                    sectionId,
                    result.section.section_title,
                    result.section.short_section_title,
                );
                commitChapterTree(next);
                toast.success('Section renamed');
            } catch (err) {
                toast.error('Could not rename section', {
                    description: err instanceof Error ? err.message : String(err),
                });
                throw err;
            } finally {
                setRenamingId(null);
            }
        },
        [book.id, commitChapterTree],
    );

    const renameChapterHandler = useCallback(
        async (chapterId: string, titles: { title: string; shortTitle: string }) => {
            const chapter = findChapterById(optimisticRef.current, chapterId);
            if (!chapter) {
                return;
            }

            const validated = validateRenameTitles(titles.title, titles.shortTitle);
            if (typeof validated === 'string') {
                toast.error('Could not rename chapter', { description: validated });
                throw new Error(validated);
            }

            try {
                setRenamingId(chapterId);
                const result = await renameMaChapter(chapter.ma_zybook_chapter_id, {
                    chapter_title: validated.title,
                    short_chapter_title: validated.shortTitle,
                });
                const next = applyChapterRename(
                    optimisticRef.current,
                    chapterId,
                    result.chapter.chapter_title,
                    result.chapter.chapter_short_title,
                );
                commitChapterTree(next);
                toast.success('Chapter renamed');
            } catch (err) {
                toast.error('Could not rename chapter', {
                    description: err instanceof Error ? err.message : String(err),
                });
                throw err;
            } finally {
                setRenamingId(null);
            }
        },
        [commitChapterTree],
    );

    const setSectionReleaseExclusionHandler = useCallback(
        async (sectionId: string, payload: { placement?: MaPlacement; testBankPlacement?: MaPlacement }) => {
            const section = findSectionById(optimisticRef.current, sectionId);
            if (!section) {
                return;
            }

            const hasSectionFlag = payload.placement !== undefined;
            const hasTestBankFlag = payload.testBankPlacement !== undefined;
            if (hasSectionFlag === hasTestBankFlag) {
                return;
            }

            const previous = optimisticRef.current;
            const optimisticPlacement = payload.placement ?? section.placement;
            const optimisticTestBankPlacement = hasSectionFlag
                ? (payload.placement ?? section.placement)
                : (payload.testBankPlacement ?? section.testBankPlacement);

            try {
                setExcludingId(sectionId);
                commitChapterTree(applySectionReleaseExclusion(previous, sectionId, optimisticPlacement, optimisticTestBankPlacement));

                const result = await setMaSectionReleaseExclusion(
                    book.zybook_id,
                    Number.parseInt(sectionId, 10),
                    hasSectionFlag ? { placement: payload.placement! } : { test_bank_placement: payload.testBankPlacement! },
                );

                commitChapterTree(
                    applySectionReleaseExclusion(
                        optimisticRef.current,
                        sectionId,
                        result.section.placement,
                        result.section.test_bank_placement,
                    ),
                );
                toast.success(
                    result.section.placement === 'excluded' || result.section.test_bank_placement === 'excluded'
                        ? 'Excluded from release'
                        : 'Included in release',
                );
            } catch (err) {
                commitChapterTree(previous);
                toast.error('Could not update release exclusion', {
                    description: err instanceof Error ? err.message : String(err),
                });
                throw err;
            } finally {
                setExcludingId(null);
            }
        },
        [book.zybook_id, commitChapterTree],
    );

    const setChapterReleaseExclusionHandler = useCallback(
        async (chapterId: string, placement: MaPlacement) => {
            const chapter = findChapterById(optimisticRef.current, chapterId);
            if (!chapter) {
                return;
            }

            const previous = optimisticRef.current;

            try {
                setExcludingId(chapterId);
                commitChapterTree(applyChapterReleaseExclusion(previous, chapterId, placement));

                await setMaChapterReleaseExclusion(book.zybook_id, chapter.ma_zybook_chapter_id, placement);
                toast.success(placement === 'excluded' ? 'Chapter excluded from release' : 'Chapter included in release');
            } catch (err) {
                commitChapterTree(previous);
                toast.error('Could not update chapter release exclusion', {
                    description: err instanceof Error ? err.message : String(err),
                });
                throw err;
            } finally {
                setExcludingId(null);
            }
        },
        [book.zybook_id, commitChapterTree],
    );

    const markSectionSynced = useCallback(
        (sectionId: string, syncedAt = new Date().toISOString()) => {
            const next = optimisticRef.current.map(chapter => ({
                ...chapter,
                sections: chapter.sections.map(section =>
                    section.id === sectionId
                        ? { ...section, canonicalSectionLastUpdated: syncedAt, documentLastEditedAt: syncedAt }
                        : section,
                ),
            }));
            commitChapterTree(next);
        },
        [commitChapterTree],
    );

    const syncSection = useCallback(
        async (sectionId: string) => {
            const section = findSectionById(optimisticRef.current, sectionId);
            if (!section) return;
            try {
                setSyncingSectionId(sectionId);
                if (section.number.startsWith('0.')) {
                    await syncAboutPageByDocumentId(Number(section.id), book.id, {
                        canonicalSectionId: section.canonicalSectionId,
                        sectionNumber: Number(section.number.split('.')[1]),
                        title: section.title,
                    });
                } else {
                    await syncSectionByDocumentId(Number(section.id), book.id);
                }
                markSectionSynced(sectionId);
                toast.success(`Section ${section.number} synced`);
            } catch (err) {
                toast.error(`Could not sync section ${section.number}`, {
                    description: err instanceof Error ? err.message : String(err),
                });
            } finally {
                setSyncingSectionId(null);
            }
        },
        [book.id, markSectionSynced],
    );

    const data = useMemo<BookTocData>(
        () => ({
            book,
            optimisticChapters,
            aboutChapter,
            contentChapters,
            unplacedSections,
            deletedSections,
            sharedDocuments,
            showSync,
            canManageToc,
            dirty,
            expandedChapterIds,
            setExpandedChapterIds,
        }),
        [
            book,
            optimisticChapters,
            aboutChapter,
            contentChapters,
            unplacedSections,
            deletedSections,
            sharedDocuments,
            showSync,
            canManageToc,
            dirty,
            expandedChapterIds,
        ],
    );

    const busy = useMemo<BookTocBusy>(
        () => ({
            isBusy,
            inFlight,
            trashingSectionId,
            trashingChapterId,
            renamingId,
            excludingId,
            syncingSectionId,
        }),
        [isBusy, inFlight, trashingSectionId, trashingChapterId, renamingId, excludingId, syncingSectionId],
    );

    const actions = useMemo<BookTocActions>(
        () => ({
            mergeSectionAfterAdd: mergeSectionAfterAddHandler,
            removeUnplacedSection,
            removeDeletedSection,
            addDeletedSection,
            mergeSharedDocuments: mergeSharedDocumentsHandler,
            trashSection: trashSectionHandler,
            trashChapter: trashChapterHandler,
            renameSection: renameSectionHandler,
            renameChapter: renameChapterHandler,
            syncSection,
            markSectionSynced,
            setSectionReleaseExclusion: setSectionReleaseExclusionHandler,
            setChapterReleaseExclusion: setChapterReleaseExclusionHandler,
            getOptimisticChapters,
            getDragSnapshot,
            beginDragSnapshot,
            previewSectionDrop,
            previewChapterDrop,
            revertDragSnapshot,
            clearDragSnapshot,
            onSectionDrop,
            onChapterDrop,
            confirmChapterDrop,
        }),
        [
            mergeSectionAfterAddHandler,
            removeUnplacedSection,
            removeDeletedSection,
            addDeletedSection,
            mergeSharedDocumentsHandler,
            trashSectionHandler,
            trashChapterHandler,
            renameSectionHandler,
            renameChapterHandler,
            syncSection,
            markSectionSynced,
            setSectionReleaseExclusionHandler,
            setChapterReleaseExclusionHandler,
            getOptimisticChapters,
            getDragSnapshot,
            beginDragSnapshot,
            previewSectionDrop,
            previewChapterDrop,
            revertDragSnapshot,
            clearDragSnapshot,
            onSectionDrop,
            onChapterDrop,
            confirmChapterDrop,
        ],
    );

    return (
        <BookTocDataContext.Provider value={data}>
            <BookTocBusyContext.Provider value={busy}>
                <BookTocActionsContext.Provider value={actions}>{children}</BookTocActionsContext.Provider>
            </BookTocBusyContext.Provider>
        </BookTocDataContext.Provider>
    );
}

export function BookTocStateProvider({ children }: { children: ReactNode }) {
    const matches = useMatches();
    const bookTocMatch = matches.find(match => match.id === 'book-toc');
    const loaderData = useRouteLoaderData(bookTocMatch?.id ?? '') as BookTocLoaderData | undefined;

    if (!bookTocMatch || !loaderData) {
        return <>{children}</>;
    }

    return (
        <BookTocStateActiveProvider
            key={loaderData.book.id}
            book={loaderData.book}
            initialChapters={loaderData.chapters}
            initialUnplacedSections={loaderData.unplacedSections}
            initialDeletedSections={loaderData.deletedSections}
            initialSharedDocuments={loaderData.sharedDocuments}
        >
            {children}
        </BookTocStateActiveProvider>
    );
}
